import React, { useEffect } from 'react';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import { RecoilRoot, useRecoilState } from 'recoil';
import { HelmetProvider } from 'react-helmet-async';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { authState, IAuthState } from './atoms/authState';
import { config } from './config';
import { decodeToken, isExpired } from 'react-jwt';
import { ConfigProvider, theme } from 'antd';
import koKR from 'antd/es/locale/ko_KR';

import 'antd/dist/reset.css';

import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import OfficeLayout from './components/OfficeLayout';
import { themeState } from './atoms/themeState';

const Users = React.lazy(() => import('./pages/Users'));
const AllowedEmails = React.lazy(() => import('./pages/AllowedEmails'));
const InviteCodes = React.lazy(() => import('./pages/InviteCodes'));
const Organizations = React.lazy(() => import('./pages/Organizations'));
const AppVersions = React.lazy(() => import('./pages/AppVersions'));
const Banners = React.lazy(() => import('./pages/Banners'));
const Managers = React.lazy(() => import('./pages/Managers'));
const Products = React.lazy(() => import('./pages/Products'));
const Login = React.lazy(() => import('./pages/Login'));

export const queryClient = new QueryClient();

const { defaultAlgorithm, darkAlgorithm } = theme;

function AppRoute() {
  const navigate = useNavigate();
  const [auth, setAuth] = useRecoilState(authState);
  const [theme] = useRecoilState(themeState);

  const { isLogged } = auth;

  useEffect(() => {
    // 1. 로그인 상태 확인
    if (!isLogged) {
      // 2. 토큰 유무 확인.
      const accessToken = localStorage.getItem(config.apiToken);
      if (accessToken) {
        const isApiTokenExpired = isExpired(accessToken);
        // API 토큰 유효함 (로그인 진행)
        if (!isApiTokenExpired) {
          const myDecodedToken = decodeToken(accessToken) as {
            manager: IAuthState;
          };

          setAuth({
            ...myDecodedToken.manager,
            isLogged: true,
          });
        }
      } else {
        navigate('/login');
      }
    }
  }, [navigate, setAuth, isLogged]);

  return (
    <ConfigProvider
      locale={koKR}
      theme={{
        algorithm: theme === 'dark' ? darkAlgorithm : defaultAlgorithm,
      }}
    >
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<OfficeLayout />}>
          <Route path="allowed-emails" element={<AllowedEmails />} />
          <Route path="invite-codes" element={<InviteCodes />} />
          <Route path="users" element={<Users />} />
          <Route path="organizations" element={<Organizations />} />
          <Route path="app-versions" element={<AppVersions />} />
          <Route path="banners" element={<Banners />} />
          <Route path="products" element={<Products />} />
          <Route path="managers" element={<Managers />} />
        </Route>
      </Routes>
    </ConfigProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <RecoilRoot>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools initialIsOpen={false} />
          <BrowserRouter>
            <AppRoute />
          </BrowserRouter>
        </QueryClientProvider>
      </RecoilRoot>
    </HelmetProvider>
  );
}

export default App;
